/**
 * What the product calls itself — the one place it is spelled (#782).
 *
 * The name is **ReqventOS**, and the domain it lives on is `reqventos.com`. It replaces "NEO",
 * which was never the product's name: NEO Catering GmbH is a catering business that *uses* this
 * product, and every other caterer we onboarded read the two as one thing. That name survives
 * untouched wherever it means the business — see `docs/PRODUCT_NAME.md` for which occurrence is
 * which, and why.
 *
 * Rules, so this does not decay:
 *
 *  - **Customer-facing text never spells the name.** German and English strings interpolate it.
 *    In the console that happens automatically — `t()` resolves `{product}` from here without
 *    the call site passing anything. On the landing, import the constant.
 *  - **German compounds take `{productCompound}`.** The unspaced name stays intact:
 *    *ReqventOS-Postfach*. Keep the token seam for existing translated compounds.
 *  - **Only the product is named from here.** A string that means the *business* NEO Catering —
 *    its Impressum, its tenant row, its sign-off — keeps spelling it, because that is its own
 *    name.
 *  - Shared by `web/app` and `web/landing`, which are separate Vite apps outside the pnpm
 *    workspace. A relative import is the whole mechanism; there is deliberately no package.
 */

/**
 * The stem remains for compatibility with existing imports. Artwork uses the supplied outlined
 * Ribbon R paths; all text and accessible alternatives use `PRODUCT_NAME_FULL`.
 */
export const PRODUCT_NAME = "Reqvent";

/**
 * The second half. It is an initialism, not a word, so it is never case-transformed on the way to
 * the screen: lowercasing it produces "os", which a screen reader reads as a word.
 */
export const PRODUCT_SUFFIX = "OS";

/** The name as it is said and written. This is what `{product}` resolves to. */
export const PRODUCT_NAME_FULL = `${PRODUCT_NAME}${PRODUCT_SUFFIX}`;

/**
 * The name inside a German compound — *ReqventOS-Adresse*. The following noun supplies its own
 * hyphen; the product name itself stays unspaced and unhyphenated.
 */
export const PRODUCT_NAME_COMPOUND = PRODUCT_NAME_FULL;
